import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast, Card } from '../V2Components';
import FieldErrorList from './FieldErrorList';

// Client-side mirrors of the backend PoleType / ProtectionType enums
// (models/enums.py). Values must match the enum string values exactly —
// Pydantic does exact-value validation, so any drift 400s a save. The backend
// test test_pole_and_protection_enum_values_match_expected_contract pins them.
export const POLE_TYPES = ['Single-pole', 'Double-pole', 'Tandem', 'Quad'];
export const PROTECTION_TYPES = ['Standard', 'GFCI', 'AFCI', 'Dual-function'];

// Poles that draw 240V and occupy two slots.
const DOUBLE_POLES = ['Double-pole', 'Quad'];

export const VOLTAGES = [120, 240];

const EMPTY_FORM = {
    label: '',
    position: '',
    max_amps: '',
    pole_type: '',
    protection_type: '',
    voltage: '',
    serves: '',
    notes: '',
};

/**
 * Create/edit form for a Circuit under a panel, rendered as an inline in-page
 * card (matching the Add Utility pattern in HouseDetails.jsx) — the form sits
 * inside the panel card, above the board, so the board behind stays put with no
 * overlay. Returns null when closed; the parent (ElectricalTab) owns open/close.
 *
 * Modes disambiguated by `circuit.id` (like PanelForm / RoomPaintModal).
 *
 * Props:
 *   isOpen   - boolean
 *   onClose  - () => void
 *   onSave   - (payload) => Promise<void>  — parent owns the API call
 *   onDelete - () => Promise<void>  — parent owns the delete; only shown in edit mode
 *   circuit  - existing Circuit row for edit mode, null for create
 *   defaultPosition - slot number to pre-fill in create mode (set when the
 *                 user clicks a blank slot on the board); ignored in edit mode.
 *   saving   - boolean  — disables the form during any save/delete in flight
 *   fieldErrors - array of Pydantic 422 error objects ({loc, msg}) to surface
 *                 inline; empty/undefined renders nothing.
 */
const CircuitForm = ({ isOpen, onClose, onSave, onDelete, circuit, defaultPosition = null, saving, fieldErrors = [] }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    // Tracks whether the user has hand-edited voltage / max_amps, so the
    // pole-type smart defaults never clobber a value they typed. Reset on every
    // (re)open so a fresh form starts with clean defaults. Prefill in edit mode
    // sets values WITHOUT marking them touched — those survive unless the user
    // changes pole_type, which is the only trigger that re-applies a default.
    const [touched, setTouched] = useState({ voltage: false, max_amps: false });

    // Reset on every (re)open DURING RENDER, not in a useEffect. The component
    // stays mounted while closed (it returns null), so formData still holds the
    // last submission; an effect-based reset commits those stale values to the
    // DOM first and clears them a tick later. Input that lands in that window is
    // lost — a fill of the same value as the stale one ("5" over a stale "5")
    // fires no React onChange, then the reset blanks it. Setting state while
    // rendering makes React re-render before committing, so stale values never
    // reach the DOM.
    const openKey = isOpen ? `${circuit?.id ?? 'new'}:${defaultPosition ?? ''}` : null;
    const [prevOpenKey, setPrevOpenKey] = useState(null);
    if (openKey !== prevOpenKey) {
        setPrevOpenKey(openKey);
        if (openKey !== null) {
            setTouched({ voltage: false, max_amps: false });
            if (circuit && circuit.id) {
                setFormData({
                    label: circuit.label || '',
                    position: circuit.position ?? '',
                    max_amps: circuit.max_amps ?? '',
                    pole_type: circuit.pole_type || '',
                    protection_type: circuit.protection_type || '',
                    voltage: circuit.voltage ?? '',
                    serves: circuit.serves || '',
                    notes: circuit.notes || '',
                });
            } else {
                setFormData({ ...EMPTY_FORM, position: defaultPosition ?? '' });
            }
        }
    }

    const isEdit = !!(circuit && circuit.id);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // Mark a numeric field as user-edited so smart defaults leave it alone.
    const handleTouchedChange = (field, value) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        handleChange(field, value);
    };

    // Smart defaults: a double-pole (or quad) breaker is 240V; a single-pole or
    // tandem is 120V and typically a 20A breaker. Only fill fields the user
    // hasn't touched, so we never fight a manual edit. Driven off the pole
    // select's onChange (NOT a formData effect, which would re-fire on prefill).
    const handlePoleTypeChange = (value) => {
        setFormData((prev) => {
            const next = { ...prev, pole_type: value };
            if (DOUBLE_POLES.includes(value)) {
                if (!touched.voltage) next.voltage = 240;
            } else if (value === 'Single-pole' || value === 'Tandem') {
                if (!touched.voltage) next.voltage = 120;
                if (!touched.max_amps && prev.max_amps === '') next.max_amps = 20;
            }
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.label.trim()) {
            toast.error('Circuit name is required.');
            return;
        }
        const payload = {
            label: formData.label.trim(),
            position: formData.position === '' ? null : Number(formData.position),
            max_amps: formData.max_amps === '' ? null : Number(formData.max_amps),
            pole_type: formData.pole_type || null,
            protection_type: formData.protection_type || null,
            voltage: formData.voltage === '' ? null : Number(formData.voltage),
            serves: formData.serves.trim() || null,
            notes: formData.notes.trim() || null,
        };
        onSave(payload);
    };

    if (!isOpen) return null;

    const title = isEdit ? 'Edit Circuit' : 'Add Circuit';
    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    return (
        <Card className="p-5 mb-4 bg-[var(--bg-hover)]">
            <h4 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-3">
                {title}
            </h4>
            <form onSubmit={handleSubmit} aria-label={title} className="space-y-4">
                <div>
                    <label htmlFor="circuit-label" className={labelClass}>
                        Name <span className="text-[var(--error)]">*</span>
                    </label>
                    <input
                        id="circuit-label"
                        value={formData.label}
                        onChange={(e) => handleChange('label', e.target.value)}
                        placeholder="e.g. Kitchen counter, Bedroom lights"
                        className={inputClass}
                        required
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="circuit-position" className={labelClass}>Slot / Position</label>
                        <input
                            id="circuit-position"
                            type="number"
                            min="1"
                            max="999"
                            value={formData.position}
                            onChange={(e) => handleChange('position', e.target.value)}
                            placeholder="12"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor="circuit-max-amps" className={labelClass}>Max Amps</label>
                        <input
                            id="circuit-max-amps"
                            type="number"
                            min="1"
                            max="200"
                            value={formData.max_amps}
                            onChange={(e) => handleTouchedChange('max_amps', e.target.value)}
                            placeholder="20"
                            className={inputClass}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="circuit-pole-type" className={labelClass}>Form Factor / Poles</label>
                        <select
                            id="circuit-pole-type"
                            value={formData.pole_type}
                            onChange={(e) => handlePoleTypeChange(e.target.value)}
                            className={inputClass}
                        >
                            <option value="">—</option>
                            {POLE_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="circuit-protection-type" className={labelClass}>Protection Type</label>
                        <select
                            id="circuit-protection-type"
                            value={formData.protection_type}
                            onChange={(e) => handleChange('protection_type', e.target.value)}
                            className={inputClass}
                        >
                            <option value="">—</option>
                            {PROTECTION_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="circuit-voltage" className={labelClass}>Voltage</label>
                        <select
                            id="circuit-voltage"
                            value={formData.voltage}
                            onChange={(e) => handleTouchedChange('voltage', e.target.value)}
                            className={inputClass}
                        >
                            <option value="">—</option>
                            {VOLTAGES.map((v) => (
                                <option key={v} value={v}>{v}V</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label htmlFor="circuit-serves" className={labelClass}>Serves</label>
                    <textarea
                        id="circuit-serves"
                        value={formData.serves}
                        onChange={(e) => handleChange('serves', e.target.value)}
                        placeholder="e.g. Dishwasher + disposal"
                        rows={2}
                        className={`${inputClass} resize-none`}
                    />
                </div>

                <div>
                    <label htmlFor="circuit-notes" className={labelClass}>Notes</label>
                    <textarea
                        id="circuit-notes"
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Anything else worth recording..."
                        rows={2}
                        className={`${inputClass} resize-none`}
                    />
                </div>

                <FieldErrorList errors={fieldErrors} />

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                    </button>
                </div>

                {/* Delete affordance — edit mode only. Opens the shared
                    ConfirmationModal (owned by the parent); no longer a nested
                    two-step, since the form is inline rather than an overlay. */}
                {isEdit && onDelete && (
                    <div className="pt-2 border-t border-[var(--border)]">
                        <button
                            type="button"
                            onClick={onDelete}
                            disabled={saving}
                            className="flex items-center gap-2 text-sm text-[var(--error)] hover:opacity-80 transition disabled:opacity-50"
                        >
                            <Trash2 size={16} />
                            Delete circuit
                        </button>
                    </div>
                )}
            </form>
        </Card>
    );
};

export default CircuitForm;
